/**
 * Distribute Tags Tool
 *
 * Distributes selected tags evenly between the two outermost tags along
 * the dominant axis of their spread (BIM's "Distribute Tags"). Runs once
 * at activation with the tags already selected, then returns to Select.
 */
import { BaseTool } from './BaseTool';
import { getSelectedElements } from '../utils/contextualModelUtils';
import { TagElement } from './TagTool';
import i18n from '../i18n';

export class DistributeTagsTool extends BaseTool {
    id = 'distribute_tags';
    name = 'Distribute Tags';
    cursor = 'default';

    protected onActivate(): void {
        const tags = getSelectedElements()
            .filter(el => el.category === 'Tags')
            .map(el => el.id);

        if (tags.length < 3) {
            this.notify(i18n.t('tools.notify.distributeTagsSelectAtLeast3'), 'warning');
            // exitToSelect captures the store BEFORE deactivate() nulls the
            // context; the old deactivate(); this.context... chain silently
            // no-oped and left this tool stuck active.
            this.exitToSelect();
            return;
        }

        this.distributeTags(tags);
        this.exitToSelect();
    }

    private distributeTags(tagIds: string[]) {
        if (!this.context) return;

        const elements = tagIds
            .map(id => this.context!.bimStore.project.elements.get(id) as TagElement | undefined)
            .filter((e): e is TagElement => !!e && !!e.geometryData?.position);

        if (elements.length < 3) return;

        // Distribute along the dominant axis of the tag spread: horizontal
        // rows of tags (the common case) spread on X, vertical columns on Y.
        let spreadX = 0, spreadY = 0;
        for (let i = 1; i < elements.length; i++) {
            spreadX += Math.abs(elements[i].geometryData.position[0] - elements[i - 1].geometryData.position[0]);
            spreadY += Math.abs(elements[i].geometryData.position[1] - elements[i - 1].geometryData.position[1]);
        }
        const axis: 0 | 1 = spreadX >= spreadY ? 0 : 1;

        elements.sort((a, b) =>
            a.geometryData.position[axis] - b.geometryData.position[axis]);

        const first = elements[0].geometryData.position[axis];
        const last = elements[elements.length - 1].geometryData.position[axis];
        const gap = (last - first) / (elements.length - 1);

        elements.forEach((el, index) => {
            if (index === 0 || index === elements.length - 1) return; // Keep the outermost tags pinned

            const newPos: [number, number, number] = [...el.geometryData.position];
            newPos[axis] = first + gap * index;

            this.context!.bimStore.updateElement(el.id, {
                geometryData: { ...el.geometryData, position: newPos }
            } as any);
        });

        this.notify(i18n.t('tools.notify.tagsDistributed', { count: elements.length, orientation: axis === 0 ? i18n.t('tools.notify.orientHorizontal') : i18n.t('tools.notify.orientVertical'), gap: Math.round(gap * 1000) }),
            'success',
        );
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}
